import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';

const SECTIONS = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    icon: 'ri-database-2-line',
    content: [
      {
        heading: 'Information You Provide',
        body: 'When you subscribe to our newsletter, use our contact form, or otherwise interact with CallOfDutyNext.com, we may collect your email address and any other information you choose to provide.',
      },
      {
        heading: 'Automatically Collected Information',
        body: 'When you visit our site, we may automatically collect certain information about your device and usage, including your IP address, browser type, operating system, referring URLs, pages viewed, and the dates and times of your visits. This data is collected through cookies and similar tracking technologies.',
      },
    ],
  },
  {
    id: 'how-we-use-information',
    title: 'How We Use Your Information',
    icon: 'ri-settings-4-line',
    bullets: [
      'To send you the newsletter and other communications you have opted into.',
      'To respond to your inquiries submitted through our contact form.',
      'To analyze and improve site performance and user experience.',
      'To detect, prevent, and address technical or security issues.',
      'To comply with applicable laws and regulations.',
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies & Tracking Technologies',
    icon: 'ri-cookie-line',
    content: [
      {
        heading: 'What Are Cookies?',
        body: 'Cookies are small text files stored on your device when you visit a website. They help us remember your preferences and understand how our site is used.',
      },
      {
        heading: 'Types of Cookies We Use',
        body: 'We use analytics cookies (e.g., Google Analytics) to understand how visitors interact with our site. You may disable cookies through your browser settings, though some features of the site may not function as intended.',
      },
    ],
  },
  {
    id: 'third-party-services',
    title: 'Third-Party Services',
    icon: 'ri-links-line',
    content: [
      {
        heading: 'Analytics',
        body: 'We use Google Analytics to collect anonymous usage data. Google\'s privacy policy governs how this data is collected and used. You can opt out via the Google Analytics opt-out browser add-on.',
      },
      {
        heading: 'External Links',
        body: 'Our site may contain links to third-party websites, including callofduty.com and other external sources. We are not responsible for the privacy practices or content of those sites and encourage you to review their respective privacy policies.',
      },
    ],
  },
  {
    id: 'data-retention',
    title: 'Data Retention',
    icon: 'ri-time-line',
    content: [
      {
        heading: 'Newsletter Subscribers',
        body: 'We retain your email address for as long as you remain subscribed. You may unsubscribe at any time via the link in any email we send you.',
      },
      {
        heading: 'Contact Form Data',
        body: 'Messages submitted via our contact form are retained only as long as necessary to respond to your inquiry and resolve any follow-up matters.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    icon: 'ri-shield-user-line',
    bullets: [
      'Access the personal information we hold about you.',
      'Request correction of inaccurate data.',
      'Request deletion of your personal data.',
      'Opt out of marketing communications at any time.',
      'Lodge a complaint with your local data protection authority.',
    ],
    suffix: 'To exercise any of these rights, please contact us through our Contact page.',
  },
  {
    id: 'childrens-privacy',
    title: 'Children\'s Privacy',
    icon: 'ri-parent-line',
    content: [
      {
        heading: '',
        body: 'CallOfDutyNext.com is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us immediately and we will take steps to delete it.',
      },
    ],
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    icon: 'ri-refresh-line',
    content: [
      {
        heading: '',
        body: 'We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make changes, we will update the "Last Updated" date at the top of this page. Your continued use of the site after any changes constitutes your acceptance of the revised policy.',
      },
    ],
  },
];

const privacySchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy — CallOfDutyNext.com',
    description:
      'Read the CallOfDutyNext.com Privacy Policy. Learn how we collect, use, and protect your personal information when you visit our site or subscribe to our newsletter.',
    url: `${SITE_URL}/privacy-policy`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy-policy` },
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="Privacy Policy | CallOfDutyNext.com"
        description="Read the CallOfDutyNext.com Privacy Policy. Learn how we collect, use, and protect your personal information when you visit our site or subscribe to our newsletter."
        keywords="CallOfDutyNext privacy policy, data collection, cookies, newsletter privacy"
        canonical="/privacy-policy"
        noIndex={false}
        schema={privacySchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* Page header */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-5xl mx-auto px-6 py-10">
            <nav className="flex items-center gap-2 text-xs text-[#555] mb-4">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-[#444]">Privacy Policy</span>
            </nav>
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-4xl md:text-5xl uppercase tracking-wider leading-none mb-3">
              Privacy Policy
            </h1>
            <p className="text-[#555] text-sm">
              Last Updated: <strong className="text-[#777]">March 27, 2026</strong>
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

            {/* Sidebar: Table of Contents */}
            <aside className="lg:col-span-1 hidden lg:block">
              <div className="sticky top-20 border border-[#1e1e1e] bg-[#0d0d0d] p-5">
                <p className="font-['Barlow_Condensed'] font-black text-white text-xs uppercase tracking-widest mb-4">
                  Contents
                </p>
                <ul className="space-y-2">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="text-[#555] hover:text-[#00ff41] text-xs transition-colors cursor-pointer leading-snug block"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main content */}
            <main className="lg:col-span-3 space-y-10">

              {/* Intro callout */}
              <div className="border border-[#00ff41]/20 bg-[#00ff41]/5 px-6 py-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-shield-check-line text-[#00ff41] text-xl" />
                  </div>
                  <div>
                    <p className="font-['Barlow_Condensed'] font-black text-white text-base uppercase mb-1">
                      Your Privacy Matters
                    </p>
                    <p className="text-[#888] text-sm leading-relaxed">
                      CallOfDutyNext.com is an independent fan news site. We are committed to protecting
                      your personal information and being transparent about how we handle it. This policy
                      explains what data we collect, how we use it, and your rights regarding your information.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sections */}
              {SECTIONS.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-20">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#1e1e1e]">
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`${section.icon} text-[#00ff41] text-base`} />
                    </div>
                    <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-wide">
                      {section.title}
                    </h2>
                  </div>

                  {section.content?.map((block, i) => (
                    <div key={i} className="mb-4">
                      {block.heading && (
                        <h3 className="font-['Barlow_Condensed'] font-black text-[#aaa] text-sm uppercase tracking-widest mb-2">
                          {block.heading}
                        </h3>
                      )}
                      <p className="text-[#777] text-sm leading-relaxed">{block.body}</p>
                    </div>
                  ))}

                  {section.bullets && (
                    <>
                      <ul className="space-y-2 mb-4">
                        {section.bullets.map((bullet, i) => (
                          <li key={i} className="flex items-start gap-3 text-[#777] text-sm leading-relaxed">
                            <span className="mt-2 shrink-0 w-1.5 h-1.5 bg-[#00ff41]" />
                            {bullet}
                          </li>
                        ))}
                      </ul>
                      {section.suffix && (
                        <p className="text-[#666] text-sm leading-relaxed italic mt-2">
                          {section.suffix}{' '}
                          <Link to="/contact" className="text-[#00ff41] hover:underline cursor-pointer">
                            Contact us here.
                          </Link>
                        </p>
                      )}
                    </>
                  )}
                </section>
              ))}

              {/* Contact CTA */}
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] p-6 mt-10">
                <h3 className="font-['Barlow_Condensed'] font-black text-white text-lg uppercase mb-2">
                  Questions About This Policy?
                </h3>
                <p className="text-[#666] text-sm leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or how we
                  handle your personal data, please reach out to us.
                </p>
                <Link
                  to="/contact"
                  className="inline-block font-['Barlow_Condensed'] font-black text-xs tracking-widest bg-[#00ff41] text-black px-5 py-2.5 hover:bg-[#00e63a] transition-colors cursor-pointer whitespace-nowrap"
                >
                  CONTACT US
                </Link>
              </div>

              {/* Legal links */}
              <div className="flex flex-wrap gap-4 pt-4 border-t border-[#1e1e1e]">
                <Link to="/terms" className="text-[#555] hover:text-white text-xs transition-colors cursor-pointer">Terms of Use</Link>
                <span className="text-[#2a2a2a]">|</span>
                <Link to="/disclaimer" className="text-[#555] hover:text-white text-xs transition-colors cursor-pointer">Disclaimer</Link>
                <span className="text-[#2a2a2a]">|</span>
                <Link to="/about" className="text-[#555] hover:text-white text-xs transition-colors cursor-pointer">About Us</Link>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
